import React, { useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import cx from 'classnames';
import PropTypes from 'prop-types';
// Components
import AutocompleteDropdown from '../../common/autocompleteDropdown/AutocompleteDropdown';
import { ERROR_MESSAGES, VALIDATION_REGEX } from './constants';

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: 200,
  },
  select: {
    height: '50px',
    width: '205px',
  },
}));

const IndividualValueEditForm = ({
  modalType,
  currentValue,
  updatedValue,
  handleValueChange,
  inputType,
  selectOptions,
  onSave,
  error,
  headerText,
  updateValueText,
  updateValueNote,
  creditReasons,
  disableCreditReasons = false,
  selectedCreditReasonId,
  handleCreditReasonChange,
  isSubmitDisabled,
  capitalized = false,
  setError,
  showConfirmationCheckbox = false,
  handleConfirmationCheckboxChange,
  confirmCheckboxValue = false,
  selectOnInputChange,
  selectFilterOptions,
  selectLoading = false,
}) => {
  // Material styles
  const materialClasses = useStyles();
  const [labelFromModalType, setLabelFromModalType] = useState('');

  const readableModalType = () => {
    const splitString = modalType.split(/(?=[A-Z])/).join(' ');
    const capitalized = splitString.charAt(0).toUpperCase() + splitString.slice(1);
    setLabelFromModalType(capitalized);
  };

  useEffect(() => {
    readableModalType();
  }, []);

  // Email validation
  const [errorFields, setErrorFields] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const isEmailChanging = modalType === 'email';

  useEffect(() => {
    if (isEmailChanging) {
      setError(errorFields?.emailError && ERROR_MESSAGES.email);
    }
  }, [errorFields, errorFields.emailError, setError]);

  const isFieldError = (fieldId) => {
    return errorFields[`${fieldId}Error`] || false;
  };

  function validateInputField(inputField, regex, inputName) {
    const validationResult = inputField.trim().match(regex);
    setErrorFields((state) => ({
      ...state,
      [`${inputName}Error`]: !validationResult,
    }));
    setIsButtonDisabled(!validationResult);
    return validationResult;
  }

  const onBlurValidation = (event) => {
    const fieldId = event?.target?.id;
    const value = event?.target?.value;

    if (isEmailChanging) {
      validateInputField(value, VALIDATION_REGEX.email, fieldId);
    }
  };

  const handleTextInputField = (event) => {
    const value = event?.target?.value;

    if (isEmailChanging) {
      handleValueChange(value.trim().length ? value : null);
    } else {
      handleValueChange(value);
    }
  };

  return (
    <div className="individual-value-edit-form-container">
      <h1 className="individual-value-edit-form-content-header">
        {headerText || `Edit ${modalType}`}
      </h1>
      <div className="individual-value-edit-form-content">
        <div className="individual-value-edit-form-content-row">
          <h1 className="individual-value-edit-form-content-row-label">Current value</h1>
          <p
            className={
              'individual-value-edit-form-content-input' + (capitalized ? ' capitalized' : '')
            }
          >
            {currentValue || '--'}
          </p>
        </div>
        <div className="individual-value-edit-form-content-row">
          {creditReasons && (
            <>
              <h1 className="individual-value-edit-form-content-row-label">Credit Reasons</h1>
              <div className="individual-value-edit-form-content-input">
                <FormControl variant="outlined" className={materialClasses.formControl}>
                  <AutocompleteDropdown
                    id="individual-value-edit-select"
                    selectedData={selectedCreditReasonId}
                    onChange={handleCreditReasonChange}
                    label="Status"
                    selectData={creditReasons}
                    disabled={disableCreditReasons}
                  />
                </FormControl>
              </div>
            </>
          )}
        </div>
        <div className="individual-value-edit-form-content-row">
          <h1 className="individual-value-edit-form-content-row-label">
            {updateValueText || 'Updated value'}
          </h1>
          <div className="individual-value-edit-form-content-input">
            {inputType === 'select' && (
              <FormControl variant="outlined" className={materialClasses.formControl}>
                <AutocompleteDropdown
                  id="individual-value-edit-select"
                  selectedData={updatedValue}
                  onChange={handleValueChange}
                  label={labelFromModalType || 'New value'}
                  selectData={selectOptions}
                  onInputChange={selectOnInputChange}
                  filterOptions={selectFilterOptions}
                  loading={selectLoading}
                />
              </FormControl>
            )}
            {inputType === 'text' && (
              <TextField
                id={modalType}
                variant="outlined"
                label={labelFromModalType}
                onChange={handleTextInputField}
                className={materialClasses.inputRoot}
                error={isFieldError(modalType)}
                onBlur={onBlurValidation}
                defaultValue={updatedValue}
              />
            )}
            {inputType === 'number' && (
              <TextField
                variant="outlined"
                label={labelFromModalType}
                type="currency"
                key={modalType}
                value={updatedValue}
                onChange={(event) => {
                  handleValueChange(event.target.value);
                }}
                className={materialClasses.inputRoot}
              />
            )}
          </div>
        </div>
        {updateValueNote && (
          <div className="individual-value-note-content-row">{updateValueNote}</div>
        )}
      </div>
      {showConfirmationCheckbox && (
        <div className="individual-value-edit-form-content">
          <FormGroup>
            <FormControlLabel
              required
              control={
                <Checkbox
                  value={confirmCheckboxValue}
                  onChange={handleConfirmationCheckboxChange}
                />
              }
              label="Please confirm you are okay with the selection you've made"
            />
          </FormGroup>
        </div>
      )}
      <div className="individual-value-edit-form-content">
        {error && <p className="error-text">{error}</p>}
        <button
          className={cx(
            'individual-value-edit-submit-button',
            (isEmailChanging ? isButtonDisabled : isSubmitDisabled) && 'disabled',
          )}
          onClick={() => {
            onSave();
          }}
          disabled={isEmailChanging ? isButtonDisabled : isSubmitDisabled}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

IndividualValueEditForm.propTypes = {
  modalType: PropTypes.string,
  currentValue: PropTypes.string,
  updatedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleValueChange: PropTypes.func,
  inputType: PropTypes.string,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      display: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  onSave: PropTypes.func,
  error: PropTypes.string,
  'selectOptions.map': PropTypes.number,
  headerText: PropTypes.string,
  updateValueText: PropTypes.string,
  updateValueNote: PropTypes.string,
  creditReasons: PropTypes.array,
  disableCreditReasons: PropTypes.bool,
  selectedCreditReasonId: PropTypes.number,
  handleCreditReasonChange: PropTypes.func,
  isSubmitDisabled: PropTypes.bool,
  capitalized: PropTypes.bool,
  showConfirmationCheckbox: PropTypes.bool,
  handleConfirmationCheckboxChange: PropTypes.func,
  confirmCheckboxValue: PropTypes.bool,
  selectOnInputChange: PropTypes.func,
  selectFilterOptions: PropTypes.func,
  selectLoading: PropTypes.bool,
};

IndividualValueEditForm.defaultProps = {
  isSubmitDisabled: false,
};

export default IndividualValueEditForm;
